/**
 * Contexte d'authentification : utilisateur courant, rôle, jetons JWT.
 * Lot 1 (US-03) : login / logout / restauration de session via sessionStorage.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  authApi,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
  type AuthUserDto,
} from "../api/api";

export type AccessProfile = "admin" | "manager" | "expert";
export type UserRole = AccessProfile;

export type AuthUser = AuthUserDto;

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const access = getAccessToken();
      const refresh = getRefreshToken();
      if (!access && !refresh) {
        setLoading(false);
        return;
      }

      try {
        if (access) {
          const { user: me } = await authApi.me();
          if (!cancelled) setUser(me);
        } else if (refresh) {
          const result = await authApi.refresh(refresh);
          setTokens(result.accessToken, result.refreshToken);
          if (!cancelled) setUser(result.user);
        }
      } catch {
        clearTokens();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const result = await authApi.login(identifier, password);
    setTokens(result.accessToken, result.refreshToken);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (getAccessToken()) {
        await authApi.logout();
      }
    } catch {
      // Même si l'API échoue, on efface la session locale.
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
